import React, { useEffect, useState } from "react";
import styles from "./PlaydateInviteRecipientView.module.css";
import {
    PlaydateInvite,
    ResponseType,
} from "../../../gql/types";
import TimeSelectionSelectionHeader from "../../time/TimeSelectionSelectionHeader";
import { convertScheduleOptionsToDayPartSelections, convertScheduleOptionsToProposedDates } from "../../../service/playdate/converters/converter";
import ActionButton from "../../button/ActionButton";
import UnderlineButton from "../../button/UnderlineButton";
import { DayPartSelection, TimeSelectionRow } from "../../time/types";
import TimeSelectionSelectionView from "../../time/TimeSelectionSelectionView";
import TextAreaInput from "../../input/TextAreaInput";
import PlaydateInviteBaseView from "./PlaydateInviteBaseView";
import PlaydateInviteGuestResponse from "../PlaydateInviteGuestResponse";

export type PlaydateInviteGenericResponseViewProps = {
    invite: PlaydateInvite;
    onBack: () => void;
    onBackToDetails: () => void;
    onSubmitResponse: (
        type: ResponseType,
        selectedOptions: DayPartSelection[] | TimeSelectionRow[] | null,
        message: string
    ) => Promise<void>;
    loading?: boolean;
    baseZIndex?: number;
};

const PlaydateInviteGenericResponseView: React.FC<PlaydateInviteGenericResponseViewProps> = ({
    invite,
    onBack,
    onBackToDetails,
    onSubmitResponse,
    loading = false,
    baseZIndex,
}) => {
    const viewerResponse = invite.viewerResponse;
    const [userSelection, setUserSelection] = useState<DayPartSelection[] | TimeSelectionRow[]>(
        viewerResponse?.selectedScheduleOptions
            ? convertScheduleOptionsToDayPartSelections(viewerResponse.selectedScheduleOptions)
            : [],
    );
    const [responseMessage, setResponseMessage] = useState<string>("");

    const handleSubmit = (type: ResponseType) => {
        const selectedOptions = type === ResponseType.Accept ? userSelection : null;
        onSubmitResponse(type, selectedOptions, responseMessage);
    };

    const infoText = viewerResponse && viewerResponse !== null
        ? "You've responded to this invite previously — update if needed."
        : "Please select as many options as possible below to share the dates and times that work best for you with the host."

    const header = viewerResponse && viewerResponse !== null
        ? "Your Updated Availability"
        : "Proposed Dates & Times"

    const primaryActionStr = viewerResponse && viewerResponse !== null
        ? (viewerResponse.responseType === ResponseType.Accept
            ? "Confirm Changes"
            : "Confirm Availability"
        )
        : "We'd love to join!"

    return (
        <PlaydateInviteBaseView
            baseZIndex={baseZIndex}
            onBack={onBack}
            title="Response Details"
            infoText={infoText}
            topIcon="happy"
        >
            {viewerResponse && <PlaydateInviteGuestResponse
                responseStatus={viewerResponse.responseType}
                selectedDates={
                    viewerResponse.selectedScheduleOptions ?
                        convertScheduleOptionsToDayPartSelections(viewerResponse.selectedScheduleOptions)
                        : undefined}
            />}
            <div className={styles.sectionHeader}>
                <TimeSelectionSelectionHeader
                    header={header}
                    subtitle={"We have selected the date and time based on your availability.  Tap the icons below to change your preferred options."}
                />
            </div>
            <TimeSelectionSelectionView
                mode={"daypart"}
                proposedDates={convertScheduleOptionsToProposedDates(invite.scheduleOptions)}
                onSelectionChange={setUserSelection}
                initialTimes={userSelection}
                groupByDate={true}
            />
            <div className={styles.sectionHeader}>Message to Host:</div>
            <TextAreaInput
                message={responseMessage}
                placeholder="Please leave a message for the host (optional)"
                setMessage={setResponseMessage}
            />
            <div className={styles.buttonContainer}>
                <ActionButton
                    onClick={() => handleSubmit(ResponseType.Accept)}
                    disabled={userSelection.length === 0}
                    hasLoadingSignal={loading}
                >
                    {primaryActionStr}
                </ActionButton>
                {(!viewerResponse || viewerResponse.responseType !== ResponseType.Reject) && <ActionButton
                    onClick={() => handleSubmit(ResponseType.Reject)}
                >
                    We can't make it
                </ActionButton>}
                <UnderlineButton onClick={onBackToDetails}>
                    Back to Invite Details
                </UnderlineButton>
            </div>
        </PlaydateInviteBaseView>
    );
};

export default PlaydateInviteGenericResponseView;
